import React, { useState } from 'react';
import { CALC_ENTITY_METADATA } from '../../constants/calcEntityMetadata';

/**
 * FullCalculationModal — Global Calculation Verification Sheet
 * Displays the step-by-step mathematical flow of the entire GTS shipment.
 */
const FullCalculationModal = ({ isOpen, onClose, liveValues = {} }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSubTab, setActiveSubTab] = useState('summary');

  if (!isOpen) return null;

  // Safe formatting helper
  const fmt = (val, decimals = 2) => {
    const num = Number(val);
    if (isNaN(num) || !Number.isFinite(num)) return '0.00';
    return num.toFixed(decimals);
  };

  // Compile calculations list
  const calcList = Object.keys(CALC_ENTITY_METADATA).map((key) => {
    const meta = CALC_ENTITY_METADATA[key];
    const evaluated = meta.evaluate ? meta.evaluate(liveValues) : null;
    return {
      key,
      title: meta.title,
      category: meta.category,
      description: meta.description,
      formulaText: meta.formulaText,
      inputs: evaluated?.inputs || [],
      substitutedFormula: evaluated?.substitutedFormula || meta.formulaText,
      steps: evaluated?.steps || [],
      liveOutput: evaluated?.liveOutput !== undefined ? evaluated.liveOutput : (liveValues[meta.outputField] || '0'),
      outputLabel: evaluated?.outputLabel || meta.outputLabel || 'Output Result',
    };
  });

  // Filter calculations based on search
  const filteredCalcs = calcList.filter((item) =>
    item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const summaryCalcs = filteredCalcs.filter((c) => c.category === 'SUMMARY');
  const interestCalcs = filteredCalcs.filter(
    (c) =>
      c.category !== 'SUMMARY' &&
      (c.outputLabel.includes('totalInt') || c.key.includes('Charges') || c.key.includes('margin') || c.key.includes('advice') || c.key.includes('Duty') || c.key.includes('Welfare') || c.key.includes('Bill') || c.key.includes('Weight') || c.key.includes('Fumigation'))
  );
  const cftCalcs = filteredCalcs.filter(
    (c) =>
      c.category !== 'SUMMARY' &&
      (c.outputLabel.includes('totalCft') || c.key.includes('Cft') || c.key.includes('Rate') || c.key === 'cost')
  );

  return (
    <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1070 }}>
      <div className="modal-dialog modal-dialog-scrollable modal-xl">
        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '1rem' }}>
          
          {/* Header */}
          <div className="modal-header gtss-bg-primary text-white border-0 py-3 px-4" style={{ borderTopLeftRadius: '1rem', borderTopRightRadius: '1rem' }}>
            <div>
              <h5 className="modal-title fw-bold d-flex align-items-center gap-2">
                <span>🔍</span> GTS Landed Cost Verification Sheet
              </h5>
              <small className="opacity-75">Live step-by-step mathematical breakdown for all typed values</small>
            </div>
            <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label="Close"></button>
          </div>

          {/* Search & Navigation Tab Bar */}
          <div className="modal-body p-0">
            <div className="bg-light p-3 border-bottom d-flex flex-wrap align-items-center justify-content-between gap-3">
              {/* Tab Navigation */}
              <div className="nav nav-pills gap-2">
                <button
                  type="button"
                  className={`nav-link py-2 px-3 fw-bold rounded-pill text-uppercase ${activeSubTab === 'summary' ? 'active bg-primary' : 'text-secondary'}`}
                  style={{ fontSize: '11px' }}
                  onClick={() => setActiveSubTab('summary')}
                >
                  📊 Summary & Totals
                </button>
                <button
                  type="button"
                  className={`nav-link py-2 px-3 fw-bold rounded-pill text-uppercase ${activeSubTab === 'interest' ? 'active bg-primary' : 'text-secondary'}`}
                  style={{ fontSize: '11px' }}
                  onClick={() => setActiveSubTab('interest')}
                >
                  💸 21 Interest Components
                </button>
                <button
                  type="button"
                  className={`nav-link py-2 px-3 fw-bold rounded-pill text-uppercase ${activeSubTab === 'cft' ? 'active bg-primary' : 'text-secondary'}`}
                  style={{ fontSize: '11px' }}
                  onClick={() => setActiveSubTab('cft')}
                >
                  📐 19 CFT Components & Cost
                </button>
                <button
                  type="button"
                  className={`nav-link py-2 px-3 fw-bold rounded-pill text-uppercase ${activeSubTab === 'all' ? 'active bg-primary' : 'text-secondary'}`}
                  style={{ fontSize: '11px' }}
                  onClick={() => setActiveSubTab('all')}
                >
                  📜 Show All ({filteredCalcs.length})
                </button>
              </div>

              {/* Search Bar */}
              <div style={{ maxWidth: '300px' }} className="w-100">
                <input
                  type="text"
                  className="form-control form-control-sm rounded-pill px-3"
                  placeholder="Search formula/component..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Content Body */}
            <div className="p-4 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
              
              {/* Active Tab: Summary & Totals */}
              {activeSubTab === 'summary' && (
                <div>
                  <h6 className="fw-bold text-secondary mb-3 text-uppercase" style={{ fontSize: '12px', letterSpacing: '0.5px' }}>
                    Summary Table Row Calculations
                  </h6>
                  <div className="d-flex flex-column gap-3">
                    {summaryCalcs.length === 0 ? (
                      <p className="text-muted small">No matching summary calculations found.</p>
                    ) : (
                      summaryCalcs.map((item, idx) => <CalculationCard key={idx} calc={item} />)
                    )}
                  </div>
                </div>
              )}

              {/* Active Tab: Interest allocations */}
              {activeSubTab === 'interest' && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-bold text-secondary mb-0 text-uppercase" style={{ fontSize: '12px', letterSpacing: '0.5px' }}>
                      21 Interest Component traces (summing to totalInt)
                    </h6>
                    <span className="badge bg-dark text-warning font-monospace py-2 px-3 fs-6">
                      Current totalInt = {fmt(liveValues.totalInt || 0, 4)}
                    </span>
                  </div>
                  <div className="d-flex flex-column gap-3">
                    {interestCalcs.length === 0 ? (
                      <p className="text-muted small">No interest components matching search found.</p>
                    ) : (
                      interestCalcs.map((item, idx) => <CalculationCard key={idx} calc={item} />)
                    )}
                  </div>
                </div>
              )}

              {/* Active Tab: CFT allocations */}
              {activeSubTab === 'cft' && (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="fw-bold text-secondary mb-0 text-uppercase" style={{ fontSize: '12px', letterSpacing: '0.5px' }}>
                      19 CFT Components & Landed Unit Cost
                    </h6>
                    <div className="d-flex gap-2">
                      <span className="badge bg-secondary text-white py-2 px-3 font-monospace">
                        totalCft = {fmt(liveValues.totalCft || 0, 4)}
                      </span>
                      <span className="badge bg-primary text-white py-2 px-3 font-monospace">
                        cost = {fmt(liveValues.cost || 0, 4)}
                      </span>
                    </div>
                  </div>
                  <div className="d-flex flex-column gap-3">
                    {cftCalcs.length === 0 ? (
                      <p className="text-muted small">No CFT components matching search found.</p>
                    ) : (
                      cftCalcs.map((item, idx) => <CalculationCard key={idx} calc={item} />)
                    )}
                  </div>
                </div>
              )}

              {/* Active Tab: Show All */}
              {activeSubTab === 'all' && (
                <div className="d-flex flex-column gap-3">
                  {filteredCalcs.length === 0 ? (
                    <p className="text-muted small">No calculations found.</p>
                  ) : (
                    filteredCalcs.map((item, idx) => <CalculationCard key={idx} calc={item} />)
                  )}
                </div>
              )}

            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer bg-light border-0 py-3 px-4" style={{ borderBottomLeftRadius: '1rem', borderBottomRightRadius: '1rem' }}>
            <div className="d-flex justify-content-between w-100 align-items-center">
              <div className="small text-muted">
                * Formulas update in real-time as you edit the fields.
              </div>
              <div className="d-flex gap-2">
                <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => window.print()}>
                  🖨️ Print Sheet
                </button>
                <button type="button" className="btn btn-sm btn-primary px-4" onClick={onClose}>
                  Done
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

/**
 * CalculationCard Component
 */
const CalculationCard = ({ calc }) => {
  return (
    <div className="card shadow-sm border border-light-subtle rounded-3">
      <div className="card-header bg-white py-2 px-3 border-0 d-flex justify-content-between align-items-center">
        <div>
          <span className="badge bg-secondary-subtle text-secondary text-uppercase me-2" style={{ fontSize: '9px' }}>
            {calc.category}
          </span>
          <strong className="text-dark" style={{ fontSize: '13px' }}>{calc.title}</strong>
        </div>
        <span className="badge bg-dark text-warning font-monospace py-1.5 px-2.5" style={{ fontSize: '12px' }}>
          = {calc.liveOutput}
        </span>
      </div>
      
      <div className="card-body py-2.5 px-3 bg-light-subtle border-top border-light-subtle">
        <p className="small text-muted mb-2" style={{ fontSize: '11px' }}>{calc.description}</p>
        
        {/* Substituted Formula */}
        <div className="bg-white p-2 rounded border border-primary-subtle font-monospace text-dark mb-2" style={{ fontSize: '11.5px' }}>
          <span className="text-muted">{calc.formulaText}</span>
          <hr className="my-1 border-light-subtle" />
          <span className="fw-bold text-primary">Live Trace:</span> {calc.substitutedFormula}
        </div>

        {/* Steps breakdown */}
        {calc.steps && calc.steps.length > 0 && (
          <div className="mt-2">
            <span className="d-block small text-uppercase text-muted fw-bold mb-1" style={{ fontSize: '9px' }}>
              Execution Steps
            </span>
            <div className="d-flex flex-column gap-1">
              {calc.steps.map((step, sIdx) => (
                <div key={sIdx} className="d-flex align-items-center gap-2 p-1.5 bg-white rounded border border-light shadow-2xs" style={{ fontSize: '11px' }}>
                  <span className="badge bg-secondary text-white" style={{ fontSize: '9px', padding: '2px 5px' }}>
                    {sIdx + 1}
                  </span>
                  <span className="text-dark fw-semibold">{step.label}: </span>
                  <span className="text-primary font-monospace">{step.expr}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FullCalculationModal;
